/**
 * Session bootstrap: mount Turnstile, mint a token, hold it, hand it to every
 * API call.
 *
 * THE TOKEN IS A HEADER AND NEVER A COOKIE. `Set-Cookie` on a cacheable HTML
 * response makes Cloudflare skip HTML caching entirely, and the SEO pages are
 * the whole acquisition model. That failure is invisible until origin CPU
 * spikes, so it is worth restating where the code could drift: this module
 * writes to localStorage and to an `X-Session-Token` request header, and to
 * nothing else.
 *
 * The widget is mounted LAZILY — only when there is no live token to reuse.
 * Loading Turnstile's script on every page view of an edge-cached landing page
 * would put a third-party request on the critical path of the one screen whose
 * entire requirement is "paste and tap without waiting".
 *
 * THE WIDGET IS MANAGED, RENDERED `interaction-only` (deploy/cloudflare.md §4).
 * Most visitors never see it: Cloudflare decides silently and the token
 * arrives. A visitor Cloudflare is unsure of (a VPN, a privacy browser, the
 * in-app browser a TikTok or Instagram link opens) is handed a checkbox
 * instead of a refusal, and this module shows it to them in a small card with
 * one line of copy. This used to be an Invisible widget in a visually hidden
 * box, which had no answer for that visitor at all: Cloudflare's distrust
 * (error 600010, seen live 2026-10-05) went straight to E14, and "reload and
 * try again" was advice no reload could make true.
 */

import type { SessionResponse } from './api-types.ts';
import { t } from './i18n.ts';

const STORAGE_KEY = 'session.v1';
/** Re-mint this far before real expiry, so a request never races the clock. */
const SKEW_S = 120;
/**
 * If Turnstile has neither answered nor asked the visitor for anything by now,
 * treat it as E14 and stop waiting.
 */
const TURNSTILE_TIMEOUT_MS = 20_000;
/**
 * Once Cloudflare asks the visitor to tick the box, the wait is a person's,
 * not a script's: they may be reading, or have switched tabs. Twenty seconds
 * would take the box away from someone about to click it.
 */
const TURNSTILE_INTERACTIVE_TIMEOUT_MS = 120_000;
/** Turnstile's flexible size is 300px at its narrowest; below that, compact. */
const NARROW_VIEWPORT = '(max-width: 359px)';
const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/**
 * The card's one line, shown only while the check wants a click. It says why the box is
 * there, because a checkbox that appears from nowhere on a site that promised "paste and
 * tap" reads as a trick. It names no provider (the founder's ruling: no infrastructure or
 * provider names in UI text), and neither does E14.
 */
export const TURNSTILE_PROMPT = "We need a quick check that you're a person. Tick the box below.";

interface StoredSession {
  token: string;
  expires_at: number;
}

interface TurnstileRenderOptions {
  sitekey: string;
  callback: (token: string) => void;
  'error-callback': (code?: string) => void;
  'timeout-callback'?: () => void;
  'before-interactive-callback'?: () => void;
  'after-interactive-callback'?: () => void;
  appearance?: 'always' | 'execute' | 'interaction-only';
  // 'invisible' is a widget MODE, set in the Cloudflare dashboard. It was never
  // a size, and Turnstile rejects it here.
  size?: 'normal' | 'flexible' | 'compact';
}

interface TurnstileApi {
  /** The widget id, or undefined when Turnstile refused to render. */
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string | undefined;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

/** Raised when a session cannot be established. The UI renders E14 for it. */
export class SessionError extends Error {
  readonly state = 'E14';
  constructor(message: string) {
    super(message);
    this.name = 'SessionError';
  }
}

function now(): number {
  return Math.floor(Date.now() / 1000);
}

function read(): StoredSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { token, expires_at } = parsed as Partial<StoredSession>;
    if (typeof token !== 'string' || typeof expires_at !== 'number') return null;
    return { token, expires_at };
  } catch {
    // Private-mode Safari and storage-partitioned contexts both throw here.
    // A session we cannot persist is still a session we can hold in memory.
    return null;
  }
}

function write(session: StoredSession): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    /* memory-only session; see read() */
  }
}

let memory: StoredSession | null = null;
let inFlight: Promise<string> | null = null;

function current(): StoredSession | null {
  const candidate = memory ?? read();
  if (!candidate) return null;
  if (candidate.expires_at - SKEW_S <= now()) return null;
  memory = candidate;
  return candidate;
}

export function clearSession(): void {
  memory = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* see read() */
  }
}

/** The site key, published by the server in a meta tag. */
function sitekey(): string {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="turnstile-sitekey"]');
  return meta?.content.trim() ?? '';
}

let scriptPromise: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = TURNSTILE_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', () => {
      const api = window.turnstile;
      if (api) resolve(api);
      else reject(new SessionError('turnstile loaded without an api'));
    });
    // A blocked request fires `error` — this is the aggressive-privacy-extension
    // case E14's copy names explicitly.
    script.addEventListener('error', () => reject(new SessionError('turnstile script blocked')));
    document.head.append(script);
  });
  return scriptPromise;
}

/**
 * The card the widget lives in. It starts visually hidden, which is all most
 * visitors ever get: an `interaction-only` widget draws nothing until
 * Cloudflare wants a click. It is not `hidden` (display: none), because the
 * widget has to run in it before anyone knows whether it will be needed.
 *
 * The line is empty until then and filled on reveal: it is a live region, so
 * a screen reader announces it at the moment the box appears and never
 * mentions a check that is not happening.
 */
function mountHost(): {
  host: HTMLElement;
  slot: HTMLElement;
  reveal: () => void;
  conceal: () => void;
} {
  const host = document.createElement('div');
  host.className = 'turnstile-card u-visually-hidden';
  host.setAttribute('data-turnstile-host', 'true');
  const line = document.createElement('p');
  line.className = 'turnstile-card__text';
  line.setAttribute('role', 'status');
  const slot = document.createElement('div');
  slot.className = 'turnstile-card__widget';
  host.append(line, slot);
  document.body.append(host);
  return {
    host,
    slot,
    reveal: () => {
      host.classList.remove('u-visually-hidden');
      // In the page's language on a translated page (lib/i18n.ts).
      line.textContent = t(TURNSTILE_PROMPT);
    },
    conceal: () => {
      host.classList.add('u-visually-hidden');
      line.textContent = '';
    },
  };
}

function narrowViewport(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(NARROW_VIEWPORT).matches;
}

/**
 * Mount a Managed widget, `interaction-only`, and resolve with its token
 * exactly once.
 *
 * Three outcomes. Cloudflare passes the visitor silently: a token, and nothing
 * was ever on screen. Cloudflare wants a click: `before-interactive` reveals
 * the card and swaps the 20 s deadline for two minutes, and
 * `after-interactive` hides it again while the click is checked. Cloudflare
 * refuses, or nothing happens in time: SessionError, which the UI renders as
 * E14.
 */
function solveTurnstile(key: string): Promise<string> {
  return loadTurnstile().then(
    (api) =>
      new Promise<string>((resolve, reject) => {
        const { host, slot, reveal, conceal } = mountHost();

        let settled = false;
        let waitingOnVisitor = false;
        let widgetId: string | undefined;
        const finish = (fn: () => void) => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          fn();
          // Out of Turnstile's own call stack, and after `render` has returned
          // the id when the callback fired synchronously inside it.
          // `turnstile.remove` rather than dropping the node: a widget whose
          // node vanished stays registered, and Turnstile's own timers then
          // warn "Cannot find Widget" into the console for the rest of the visit.
          queueMicrotask(() => {
            if (widgetId !== undefined) {
              try {
                api.remove(widgetId);
              } catch {
                /* already gone; the node below is what the visitor can see */
              }
            }
            host.remove();
          });
        };
        const expire = () =>
          finish(() =>
            reject(
              new SessionError(
                waitingOnVisitor
                  ? 'turnstile timed out waiting for a click'
                  : 'turnstile timed out',
              ),
            ),
          );
        let timer = setTimeout(expire, TURNSTILE_TIMEOUT_MS);

        try {
          widgetId = api.render(slot, {
            sitekey: key,
            appearance: 'interaction-only',
            size: narrowViewport() ? 'compact' : 'flexible',
            callback: (token) => finish(() => resolve(token)),
            'error-callback': (code) =>
              finish(() => reject(new SessionError(`turnstile error ${code ?? 'unknown'}`))),
            'timeout-callback': () => finish(() => reject(new SessionError('turnstile expired'))),
            'before-interactive-callback': () => {
              if (settled) return;
              waitingOnVisitor = true;
              clearTimeout(timer);
              timer = setTimeout(expire, TURNSTILE_INTERACTIVE_TIMEOUT_MS);
              reveal();
            },
            'after-interactive-callback': () => {
              if (!settled) conceal();
            },
          });
        } catch (err) {
          finish(() =>
            reject(
              new SessionError(
                `turnstile render failed: ${err instanceof Error ? err.message : 'unknown'}`,
              ),
            ),
          );
        }
      }),
  );
}

async function mint(): Promise<string> {
  const key = sitekey();
  // WHETHER TURNSTILE IS REQUIRED IS THE SERVER'S RULING, NOT THIS CLIENT'S.
  //
  // This used to throw when no site key was published, on the reasoning that a
  // missing key is a deployment fault. It is — but refusing to even ask turns
  // "Turnstile is not configured yet" into a total outage that presents as a
  // bot check: no session, so no quota, no platform list, no badge, no
  // caption-path hint, and every submission ending in E14. Observed end to end
  // against a server whose own `SITE_TURNSTILE_REQUIRED` was false and which
  // answered 201 to the very request this code declined to send.
  //
  // So: solve the challenge when there is one to solve, send the request
  // either way, and let the server decide. If it requires a token and got
  // none, it answers 403 and E14 is then the honest state rather than a guess.
  if (!key) {
    console.warn(
      '[session] no turnstile sitekey published; minting without one. ' +
        'The server decides whether that is allowed.',
    );
  }
  const turnstileToken = key ? await solveTurnstile(key) : '';
  const res = await fetch('/api/session', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ turnstile_token: turnstileToken }),
    cache: 'no-store',
  });
  if (!res.ok) throw new SessionError(`session mint failed: ${res.status}`);
  const body = (await res.json()) as SessionResponse;
  if (typeof body.session_token !== 'string') throw new SessionError('malformed session response');
  const session: StoredSession = { token: body.session_token, expires_at: body.expires_at };
  memory = session;
  write(session);
  return session.token;
}

/**
 * The token every API call needs. Concurrent callers share one mint — a page
 * that fires /api/quota and /api/platforms together must not run the bot check
 * twice.
 */
export function sessionToken(): Promise<string> {
  const live = current();
  if (live) return Promise.resolve(live.token);
  if (!inFlight) {
    inFlight = mint().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

/** True when a token is already held, so a caller can skip an optional fetch. */
export function hasSession(): boolean {
  return current() !== null;
}

/**
 * The held token, synchronously, or null.
 *
 * Exists for exactly one caller: the instrumentation flush on `pagehide`,
 * which has no time to await a mint and must never be the reason a page is
 * held open. Everything else uses `sessionToken()`.
 */
export function peekSessionToken(): string | null {
  return current()?.token ?? null;
}
